// Main Application Entry Point

import { themeManager } from './theme.js';
import { ui } from './ui.js';
import { dataManager } from './data.js';
import './workouts.js'; // Will load workout module dynamically
import './charts.js'; // Will load charts module dynamically
import './timer.js'; // Will load timer module dynamically
import './dashboard.js'; // Will load dashboard module dynamically

class App {
  constructor() {
    this.initialized = false;
  }

  async init() {
    if (this.initialized) return;

    // Initialize theme
    themeManager.init();

    // Initialize UI
    ui.init();

    // Setup global event listeners
    this.setupEventListeners();

    // Load data
    try {
      ui.showLoading('training-content');

      await dataManager.load();

      // Dispatch data loaded event
      window.dispatchEvent(new Event('dataloaded'));
    } catch (error) {
      console.error('❌ Error initializing app:', error);
      ui.showError('training-content');
    }

    this.initialized = true;
  }

  setupEventListeners() {
    // Theme toggle buttons
    const themeButtons = document.querySelectorAll('[id*="themeToggle"], [id*="ThemeToggle"]');
    themeButtons.forEach(btn => {
      btn.addEventListener('click', () => themeManager.toggle());
    });

    // Export/Import/Clear buttons
    const exportBtn = document.getElementById('exportBtn');
    const importBtn = document.getElementById('importBtn');
    const clearBtn = document.getElementById('clearBtn');

    if (exportBtn) {
      exportBtn.addEventListener('click', () => this.exportData());
    }

    if (importBtn) {
      importBtn.addEventListener('click', () => this.importData());
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', () => this.clearData());
    }

    // File input for import
    const fileInput = document.getElementById('importFileInput');
    if (fileInput) {
      fileInput.addEventListener('change', e => this.handleImportFile(e));
    }

    // PWA install prompt
    this.setupPWA();

    // Service Worker registration
    this.registerServiceWorker();
  }

  async exportData() {
    try {
      const { storage } = await import('./storage.js');
      const data = storage.exportData();
      const dataStr = JSON.stringify(data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `workout-data-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
      alert('✅ Dati esportati con successo!');
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('❌ Errore durante l\'esportazione');
    }
  }

  importData() {
    const fileInput = document.getElementById('importFileInput');
    if (fileInput) {
      fileInput.click();
    }
  }

  async handleImportFile(event) {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async e => {
      try {
        const { storage } = await import('./storage.js');
        const data = JSON.parse(e.target.result);
        const count = storage.importData(data);
        alert(`✅ ${count} elementi importati con successo!`);

        // Refresh current tab
        window.dispatchEvent(new Event('datachanged'));
      } catch (error) {
        console.error('Error importing data:', error);
        alert('❌ Errore durante l\'importazione. File non valido.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  }

  async clearData() {
    if (!confirm('⚠️ Sei sicuro di voler cancellare tutti i dati personali? Questa azione non può essere annullata.')) {
      return;
    }

    try {
      const { storage } = await import('./storage.js');
      const count = storage.clear('workout-');
      alert(`✅ ${count} elementi cancellati.`);

      // Refresh current tab
      window.dispatchEvent(new Event('datachanged'));
    } catch (error) {
      console.error('Error clearing data:', error);
      alert('❌ Errore durante la cancellazione');
    }
  }

  setupPWA() {
    let deferredPrompt;
    window.addEventListener('beforeinstallprompt', e => {
      e.preventDefault();
      deferredPrompt = e;
    });
  }

  registerServiceWorker() {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker
          .register('/sw.js')
          .catch(err => console.error('Service Worker registration failed:', err));
      });
    }
  }
}

// Initialize app when DOM is ready
const app = new App();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => app.init());
} else {
  app.init();
}

export default app;
